// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useBrokersServiceBrokersPersonalApiSettingsPartialUpdate,
  useBrokersServiceBrokersPersonalApiSettingsRetrieve,
  useBrokersServiceBrokersPersonalApiSettingsRetrieveKey,
  useBrokersServiceBrokersPersonalRotateTokenCreate
} from '@insurance/api/queries'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TokenTypeEnum } from '@insurance/api/requests'
import { useInvalidateQueries } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { App, Button, Flex, Form, Input, Modal, Tooltip } from 'antd'
import { Key } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SecretTokenField } from './SecretTokenField'

type WebhookForm = {
  webhook: string
}

export const BrokerApiSettings = () => {
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const { invalidateQueries } = useInvalidateQueries()

  const [open, setOpen] = useState(false)
  const [form] = Form.useForm<WebhookForm>()

  const { data } = useBrokersServiceBrokersPersonalApiSettingsRetrieve(
    undefined,
    { enabled: open }
  )

  useEffect(() => {
    if (data) {
      form.setFieldValue('webhook', data.webhook ?? '')
    }
  }, [data, form])

  const refetchSettings = () =>
    invalidateQueries([useBrokersServiceBrokersPersonalApiSettingsRetrieveKey])

  const { mutate: updateSettings, isPending: isUpdating } =
    useBrokersServiceBrokersPersonalApiSettingsPartialUpdate({
      onSuccess: () => {
        message.success(t('broker_api.webhook_saved'))
        refetchSettings()
      },
      onError: (error) => showApiErrors(error, form)
    })

  const { mutate: rotateToken, isPending: isRotating } =
    useBrokersServiceBrokersPersonalRotateTokenCreate({
      onSuccess: () => {
        message.success(t('broker_api.token_rotated'))
        refetchSettings()
      },
      onError: (error) => showApiErrors(error)
    })

  const onRotate = (tokenType: TokenTypeEnum) =>
    modal.confirm({
      title: t('broker_api.rotate_confirm_title'),
      content: t('broker_api.rotate_confirm_description'),
      okText: t('button.confirm'),
      cancelText: t('button.cancel'),
      okButtonProps: { danger: true },
      onOk: () => rotateToken({ requestBody: { token_type: tokenType } })
    })

  const onSaveWebhook = () =>
    updateSettings({
      requestBody: { webhook: form.getFieldValue('webhook') || null }
    })

  return (
    <>
      <Tooltip title={t('broker_api.title')}>
        <Button
          type="text"
          icon={<Key size={20} />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>

      <Modal
        open={open}
        title={t('broker_api.title')}
        onCancel={() => setOpen(false)}
        footer={null}
        width={560}
        centered
        destroyOnHidden
      >
        <Flex vertical gap={20} style={{ marginTop: 16 }}>
          <SecretTokenField
            label={t('broker_api.secret_token')}
            value={data?.secret_token}
            rotating={isRotating}
            onRotate={() => onRotate(TokenTypeEnum.SECRET_TOKEN)}
          />

          <SecretTokenField
            label={t('broker_api.webhook_token')}
            value={data?.webhook_token}
            rotating={isRotating}
            onRotate={() => onRotate(TokenTypeEnum.WEBHOOK_TOKEN)}
          />

          <Form form={form} layout="vertical">
            <Form.Item label={t('broker_api.webhook_url')} name="webhook">
              <Input
                placeholder={t('broker_api.webhook_url_placeholder')}
                allowClear
              />
            </Form.Item>

            <Flex justify="flex-end">
              <Button
                type="primary"
                loading={isUpdating}
                onClick={onSaveWebhook}
              >
                {t('button.save')}
              </Button>
            </Flex>
          </Form>
        </Flex>
      </Modal>
    </>
  )
}
